import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { EMPLOYEE_CONFIG } from "../../../../../../constants/employeeConfig";
import { buildMasterlistWorksheet } from "../../../../../../utils/employee/buildWorksheet";
import { fetchDataEmployeeInformationOnly } from "../../../EmployeesListApi";

// =============================================================================
// FORMAT DATE
// Converts ISO date string to MM/DD/YYYY format for Excel display.
// =============================================================================
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

// =============================================================================
// PREPEND FIELDS
// EmployeeId and EmployeeName as first two columns.
// =============================================================================
const PREPEND_FIELDS = [
  { key: "employeeId", label: "EmployeeId",   width: 20, masterlistKey: "employeeId" },
  { key: "fullName",   label: "EmployeeName", width: 30, masterlistKey: "fullName" },
];

// =============================================================================
// EXCLUDED FIELDS
// These fields appear in the full employeeInformation config but are NOT
// shown in this report — matches the original function's column list.
// =============================================================================
const EXCLUDED_KEYS = [
  "effectiveFrom",
  "contractFrom",
  "contractTo",
  "dateEmployed",
  "dateRegular",
  "dateProbationary",
  "dateResigned",
  "dateContractual",
  "employmentDuration",
  "companyId",        // not in original generateDownloadEmployeeInformation
  // "destinationId",    // not in original generateDownloadEmployeeInformation
];

// =============================================================================
// GENERATE DOWNLOAD EMPLOYEE INFORMATION
// Single-sheet Excel report of employee information fields.
// Uses fetchDataEmployeeInformationOnly — a lighter fetch than the full masterlist.
//
// Difference from generateDownloadMasterlist:
//   - Single sheet only (EmployeeInformation)
//   - Different fetch function (faster, information tab only)
//   - Excludes date fields, company, and destination columns
// =============================================================================
export const generateDownloadEmployeeInformation = async (filters, showGeneralMessage) => {
  const _employees = await fetchDataEmployeeInformationOnly(filters);

  if (!_employees || _employees.length <= 0) {
    showGeneralMessage("Please wait for the employees to load", false);
    return;
  }

  const workbook = new ExcelJS.Workbook();

  // Filter out excluded fields — same column set as the original function
  const employeeInfoSection = {
    ...EMPLOYEE_CONFIG.employeeInformation,
    fields: EMPLOYEE_CONFIG.employeeInformation.fields.filter(
      (f) => !EXCLUDED_KEYS.includes(f.key)
    ),
  };

  buildMasterlistWorksheet(
    workbook,
    "EmployeeInformation",
    employeeInfoSection,
    _employees,
    {
      prependFields: PREPEND_FIELDS,
      getRowValues: (employee, fields) => {
        const source = employee.employeeInformation;
        return fields.map((f) => {
          // prependFields have masterlistKey pointing to root employee object
          if (f.masterlistKey) return employee[f.masterlistKey] ?? "";

          if (!source) return "";

          // Fields with explicit masterlistKey in config (shiftSchedule, company, vessel)
          if (f.masterlistKey) return source[f.masterlistKey] ?? "";

          // Default — remove "Id", append "Description"
          if (f.key.endsWith("Id")) {
            const baseKey = f.key.replace("Id", "");
            const descKey = `${baseKey}Description`;
            if (source[descKey] !== undefined) return source[descKey] ?? "";
          }

          // Date fields
          if (f.type === "date") return formatDate(source[f.key]);

          // Plain value
          return source[f.key] ?? "";
        });
      },
    }
  );

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, "Employee_Information.xlsx");
};
